// Small helpers used by Reports dashboard.

// ---- Date presets ----

const isoDay = (d) => d.toISOString().slice(0, 10);

export const PRESETS = [
  { key: "ttm", label: "TTM (last 12 mo)" },
  { key: "ytd", label: "Year-to-date" },
  { key: "mtd", label: "Month-to-date" },
  { key: "this_month", label: "This month" },
  { key: "last_month", label: "Last month" },
  { key: "last_year", label: "Last year" },
  { key: "all", label: "All time" },
  { key: "custom", label: "Custom…" },
];

export function rangeFor(preset, custom = null) {
  const today = new Date();
  const y = today.getFullYear(), m = today.getMonth(), d = today.getDate();
  switch (preset) {
    case "all": return { start: "", end: "" };
    case "mtd":
    case "this_month": {
      const s = new Date(y, m, 1);
      return { start: isoDay(s), end: isoDay(today) };
    }
    case "ytd": return { start: `${y}-01-01`, end: isoDay(today) };
    case "ttm": {
      const s = new Date(today); s.setFullYear(s.getFullYear() - 1); s.setDate(s.getDate() + 1);
      return { start: isoDay(s), end: isoDay(today) };
    }
    case "last_month": {
      const s = new Date(y, m - 1, 1);
      const e = new Date(y, m, 0);
      return { start: isoDay(s), end: isoDay(e) };
    }
    case "last_year": return { start: `${y - 1}-01-01`, end: `${y - 1}-12-31` };
    case "custom": return { start: custom?.start || "", end: custom?.end || "" };
    default: return { start: "", end: "" };
  }
}

// Prior equivalent-length window (used for YoY-style comparisons).
export function priorRange(range) {
  if (!range.start || !range.end) return null;
  const s = new Date(range.start), e = new Date(range.end);
  const lenDays = Math.round((e - s) / 86400000) + 1;
  const priorEnd = new Date(s); priorEnd.setDate(priorEnd.getDate() - 1);
  const priorStart = new Date(priorEnd); priorStart.setDate(priorStart.getDate() - lenDays + 1);
  return { start: isoDay(priorStart), end: isoDay(priorEnd) };
}

// ---- Trend chart presets (subset of PRESETS shown on Historic billing trend) ----
export const TREND_PRESETS = [
  { key: "this_month", label: "This month" },
  { key: "ytd", label: "Year-to-date" },
  { key: "ttm", label: "TTM (12 mo)" },
  { key: "all", label: "All time" },
];

// ---- Compare-period presets (relative to the primary range, not absolute) ----
// The primary picker uses absolute presets (This month / Last year / YTD…);
// the compare picker uses SEMANTIC presets that adapt to whatever the primary
// range is, so "Same period last year" always means the primary window shifted
// back 12 months — no more mismatched-length comparisons.
export const COMPARE_PRESETS = [
  { key: "prior_period", label: "Prior period" },
  { key: "same_last_year", label: "Same period last year" },
  { key: "same_2y_ago", label: "Same period 2 years ago" },
  { key: "custom", label: "Custom…" },
];

export function compareRangeFor(preset, primaryRange, custom = null) {
  if (preset === "custom") return { start: custom?.start || "", end: custom?.end || "" };
  if (!primaryRange || !primaryRange.start || !primaryRange.end) return { start: "", end: "" };
  const s = new Date(primaryRange.start), e = new Date(primaryRange.end);
  const shiftYears = (n) => {
    const s2 = new Date(s); s2.setFullYear(s2.getFullYear() - n);
    const e2 = new Date(e); e2.setFullYear(e2.getFullYear() - n);
    return { start: isoDay(s2), end: isoDay(e2) };
  };
  switch (preset) {
    case "prior_period": return priorRange(primaryRange) || { start: "", end: "" };
    case "same_last_year": return shiftYears(1);
    case "same_2y_ago": return shiftYears(2);
    default: return { start: "", end: "" };
  }
}

export function presetLabel(key) {
  return (PRESETS.find((p) => p.key === key) || {}).label || key;
}

// ---- CSV export ----

/**
 * Trigger a browser download of `rows` as a CSV file. `columns` is an array of
 * {key, label} pairs; `rows` is an array of objects keyed by `columns[].key`.
 */
export function downloadCsv(filename, columns, rows) {
  const esc = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [
    columns.map((c) => esc(c.label)).join(","),
    ...rows.map((r) => columns.map((c) => esc(r[c.key])).join(",")),
  ];
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}
